// React
import React, { Fragment, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import BProductGallery, { HostStyles } from './styles'
import OptimizedImage from './components/OptimizedImage'
import { LightboxGallery } from './components/LightboxGallery'

// Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft as farChevronLeft, faChevronRight as farChevronRight } from '@fortawesome/pro-regular-svg-icons'

// Shared
import { fetchPhotos } from 'shop-components/redux/actions/photo'
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import { focalPointToObjectPosition } from 'shop-components/utils/focalPointToObjectPosition'
import { useMediaQuery } from 'shared/utils/useMediaQuery'

const ProductGallery = ({
  item,
  photos,
  variationId,
  variations,
  fetchPhotos,
  enableThumbnailsMobile = 'true',
  enableThumbnailsDesktop = 'true',
  enableControlsMobile = 'true',
  enableControlsDesktop = 'true',
  infiniteScrollMobile = 'true',
  infiniteScrollDesktop = 'true',
  imageFitDesktop = 'contain',
  imageFitMobile = 'contain'
}) => {
  const listenerReady = useRef(false)
  const innerRef = useRef(null)
  const thumbnailsWrapperRef = useRef(null)
  const thumbnailsInnerRef = useRef(null)
  const isDesktop = useMediaQuery('(min-width: 577px)')

  const [currentIndex, setIndex] = useState(variationId && variations ? null : 0)
  const [showLightbox, setShowLightbox] = useState(false)

  const withInfiniteScroll = isDesktop ? infiniteScrollDesktop === 'true' : infiniteScrollMobile === 'true'
  const withControls = isDesktop ? enableControlsDesktop === 'true' : enableControlsMobile === 'true'
  const withThumbnails = isDesktop ? enableThumbnailsDesktop === 'true' : enableThumbnailsMobile === 'true'

  useEffect(() => {
    if (!item?.id) return

    fetchPhotos()
  }, [item?.id])

  useEffect(() => {
    if (photos?.length && !listenerReady.current) {
      // Reset scroll offset on gallery init to prevent invalid index to be calculated
      innerRef.current.scrollLeft = 0

      const handleInnerScroll = () => {
        const width = innerRef.current.offsetWidth
        const offset = innerRef.current.scrollLeft
        const index = Math.round(offset / width)

        handleSnapToThumbnail(index)

        setIndex(index)
      }

      innerRef.current?.addEventListener('scroll', handleInnerScroll)
      innerRef.current?.addEventListener('touchmove', handleInnerScroll)

      listenerReady.current = true

      return () => {
        innerRef.current?.removeEventListener('scroll', handleInnerScroll)
        innerRef.current?.removeEventListener('touchmove', handleInnerScroll)

        listenerReady.current = false
      }
    }
  }, [photos])

  useEffect(() => {
    if (variationId && variations) {
      const currentPhotoId = variations.find((variation) => variation.id === variationId)?.photo_id
      const currentPhotoIndex = photos.findIndex((photo) => photo.id === currentPhotoId)

      if (currentPhotoIndex > -1) {
        handleSnapToPhoto(currentPhotoIndex)
      }
    }
  }, [variationId, variations, photos])

  const handleSnapToThumbnail = (index) => {
    const width = thumbnailsInnerRef.current?.offsetWidth / photos.length
    const height = thumbnailsInnerRef.current?.offsetHeight / photos.length

    thumbnailsWrapperRef.current?.scrollTo({
      left: width * index - width,
      top: height * index - height,
      behavior: 'smooth'
    })
  }

  const handleSnapToPhoto = (index, behavior = 'smooth') => {
    const width = innerRef.current?.offsetWidth
    const offset = width * index

    innerRef.current?.scrollTo({
      left: offset,
      behavior
    })

    handleSnapToThumbnail(index)

    setIndex(index)
  }

  const handleSnapToNextPhoto = () => {
    if (currentIndex < photos.length - 1) {
      handleSnapToPhoto(currentIndex + 1)
    }

    if (currentIndex === photos.length - 1 && withInfiniteScroll) {
      handleSnapToPhoto(0)
    }
  }

  const handleSnapToPrevPhoto = () => {
    if (currentIndex > 0) {
      handleSnapToPhoto(currentIndex - 1)
    }

    if (currentIndex === 0 && withInfiniteScroll) {
      handleSnapToPhoto(photos.length - 1)
    }
  }

  const handleImageClick = () => setShowLightbox(true)

  const handleLightboxClose = () => setShowLightbox(false)

  return (
    <Fragment>
      <HostStyles />
      {!!photos?.length && (
        <BProductGallery>
          <BProductGallery.Wrapper>
            <BProductGallery.Inner ref={innerRef} noGap>
              {photos?.map((photo) => {
                const objectPosition = focalPointToObjectPosition(photo.coordinates)
                return (
                  <BProductGallery.Item key={photo.id}>
                    <BProductGallery.Image
                      src={photo.url}
                      style={{ objectPosition, cursor: 'pointer' }}
                      alt=""
                      imageFitDesktop={imageFitDesktop}
                      imageFitMobile={imageFitMobile}
                      onClick={handleImageClick}
                    />
                  </BProductGallery.Item>
                )
              })}
            </BProductGallery.Inner>
            {withControls && photos?.length > 1 && (
              <BProductGallery.Controls>
                <BProductGallery.Control
                  onClick={handleSnapToPrevPhoto}
                  data-direction="prev"
                  disabled={currentIndex === 0 && !withInfiniteScroll}
                >
                  <FontAwesomeIcon icon={farChevronLeft} />
                </BProductGallery.Control>
                <BProductGallery.Control
                  onClick={handleSnapToNextPhoto}
                  data-direction="next"
                  disabled={currentIndex === photos?.length - 1 && !withInfiniteScroll}
                >
                  <FontAwesomeIcon icon={farChevronRight} />
                </BProductGallery.Control>
              </BProductGallery.Controls>
            )}
          </BProductGallery.Wrapper>
          {withThumbnails && photos?.length > 1 && (
            <BProductGallery.Wrapper>
              <BProductGallery.Inner ref={thumbnailsWrapperRef}>
                <BProductGallery.ThumbnailsInner ref={thumbnailsInnerRef}>
                  {photos
                    .filter(({ url }) => !!url)
                    .map((photo, index) => {
                      const isActive = currentIndex === index

                      return (
                        <BProductGallery.Thumbnail
                          key={photo.id}
                          isActive={isActive}
                          data-index={index + 1}
                          data-active={isActive}
                          onClick={() => handleSnapToPhoto(index)}
                        >
                          <OptimizedImage src={photo.url} focal={photo.coordinates} alt="" width="120" height="120" />
                        </BProductGallery.Thumbnail>
                      )
                    })}
                </BProductGallery.ThumbnailsInner>
              </BProductGallery.Inner>
            </BProductGallery.Wrapper>
          )}
        </BProductGallery>
      )}
      {showLightbox && (
        <LightboxGallery
          photos={photos}
          selectedIndex={currentIndex}
          onClose={handleLightboxClose}
          itemTitle={item?.name}
        />
      )}
    </Fragment>
  )
}

ProductGallery.propTypes = {
  item: PropTypes.object,
  photos: PropTypes.array,
  variationId: PropTypes.string,
  variations: PropTypes.array,
  fetchPhotos: PropTypes.func,
  enableThumbnailsMobile: PropTypes.oneOf(['true', 'false']),
  enableThumbnailsDesktop: PropTypes.oneOf(['true', 'false']),
  enableControlsMobile: PropTypes.oneOf(['true', 'false']),
  enableControlsDesktop: PropTypes.oneOf(['true', 'false']),
  infiniteScrollMobile: PropTypes.oneOf(['true', 'false']),
  infiniteScrollDesktop: PropTypes.oneOf(['true', 'false']),
  imageFitDesktop: PropTypes.oneOf(['contain', 'cover']),
  imageFitMobile: PropTypes.oneOf(['contain', 'cover'])
}

const isBundle = (ownerType) => ownerType === 'bundles'

const shortByPositionWithBundleFirst = (photos) => (
  photos.sort((a, b) => {
    const aIsBundle = isBundle(a.owner_type)
    const bIsBundle = isBundle(b.owner_type)

    if (aIsBundle && !bIsBundle) return -1 // a is bundle, b is not
    if (!aIsBundle && bIsBundle) return 1 // b is bundle, a is not

    return a.position - b.position // both are bundles or neither is a bundle, sort by position
  })
)

const mapStateToProps = (state, props) => {
  const item = state.items.records[props.productId]
  return {
    item,
    photos: shortByPositionWithBundleFirst(item?.photos || []),
    variationId: item?.variation_id,
    variations: item?.products
  }
}

const mapDispatchToProps = (dispatch, props) => ({
  fetchPhotos: () => dispatch(fetchPhotos(props.productId))
})

const decoratedComponent = compose(
  withStore,
  withTheme,
  reduxConnect(mapStateToProps, mapDispatchToProps)
)(ProductGallery)

registerCustomElement('bq-product-gallery', decoratedComponent, {
  attributes: [
    'product-id',
    'enable-thumbnails-mobile',
    'enable-thumbnails-desktop',
    'enable-controls-mobile',
    'enable-controls-desktop',
    'infinite-scroll-mobile',
    'infinite-scroll-desktop',
    'image-fit-desktop',
    'image-fit-mobile'
  ]
})

export default decoratedComponent
